import { i18n } from './i18n'
import { storage } from './storage'
import { useAppEventsStore } from '@/stores/app-events'
import type {
  Repository,
  Collaborator,
  AIReview as AIReviewType,
  UsageStatus,
  UsageHistoryEntry,
  PaginatedUsageHistory,
  PaginatedChargesHistory,
  AnalyticsOverview,
  AnalyticsTeamMember,
  AnalyticsInsight,
  AnalyticsSyncStatus,
  AIContext,
  CommitNode,
  DiffFile,
} from '@/types'
import type { BoardConfig } from '@/features/board/columns'

export class ApiError extends Error {
  code: string | undefined
  status: number
  data: unknown

  constructor(message: string, code: string | undefined, status: number, data: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.data = data
  }
}

interface RequestOptions {
  headers?: Record<string, string>
}

interface CommitGraphOptions {
  branch?: string
  limit?: number
}

export interface AIReviewQuery {
  provider?: string
  repositoryFullName?: string
  pullRequestNumber?: string | number
}

export interface CollaboratorSearchResult {
  found: boolean
  alreadyCollaborator?: boolean
  reason?: string
  user: {
    id: string
    name: string
    email: string
    avatarUrl?: string
  }
}

export interface CommitGraphData {
  commits: CommitNode[]
  branches?: string[]
  pullRequests?: { commitShas?: string[] }[]
  defaultBranch?: string
}

export interface PRFilesData {
  files: DiffFile[]
}

export interface UsageStatusData {
  usageSpentThisPeriodBcu: number
  usageMonthlyLimitBcu: number | null
  [key: string]: unknown
}

export interface BranchInfo {
  name: string
  isDefault: boolean
}

export interface RepositoryStatsData {
  [repoId: string]: {
    fileCount: number
    [key: string]: unknown
  }
}

export interface MergeResult {
  merged: boolean
  message?: string
}

export interface CloseResult {
  closed: boolean
}

export interface ProviderLoginResult {
  login: string
}

class ApiClient {
  private baseUrl: string
  private token: string | null

  constructor() {
    this.baseUrl = window.CONFIG?.API_URL || '/api'
    this.token = storage.getItem('codeboard_token')
  }

  setToken(token: string | null): void {
    this.token = token
    if (token) {
      storage.setItem('codeboard_token', token)
    } else {
      storage.removeItem('codeboard_token')
    }
  }

  getToken(): string | null {
    return this.token
  }

  isAuthenticated(): boolean {
    return !!this.token
  }

  async request<T = unknown>(
    method: string,
    path: string,
    data: unknown = null,
    options: RequestOptions = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const config: RequestInit = { method, headers }

    if (data && method !== 'GET') {
      config.body = JSON.stringify(data)
    }

    try {
      const response = await fetch(url, config)
      const result = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          this.setToken(null)
          useAppEventsStore().emitUnauthorized()
        }
        const errMsg = typeof result.error === 'string'
          ? result.error
          : result.error?.message || result.message || 'Request failed'
        throw new ApiError(
          errMsg,
          result.error?.code || result.code,
          response.status,
          result.data,
        )
      }

      const bcuEndpoints = ['/ai/reviews', '/ai/projects/', '/analytics/insights/generate', '/ai/insights', '/ai/ci-analysis', '/ai/commit-message', '/analytics/sprint/summaries/generate']
      if (method === 'POST' && bcuEndpoints.some((ep) => path.includes(ep))) {
        useAppEventsStore().emitBcuChanged()
      }

      return result.data as T
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError(i18n.t('errors.network'), 'NETWORK_ERROR', 0)
    }
  }

  get<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, null, options)
  }

  post<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, data, options)
  }

  put<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, data, options)
  }

  delete<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, null, options)
  }

  patch<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, data, options)
  }

  async getGitHubAuthMethods() {
    return this.get('/auth/github/methods')
  }

  async getGitHubAuthUrl(method: string | null = null) {
    const query = method ? `?method=${method}` : ''
    return this.get(`/auth/github${query}`)
  }

  async getGitLabAuthMethods() {
    return this.get('/auth/gitlab/methods')
  }

  async checkGitLabVersion(baseUrl: string) {
    return this.post('/auth/gitlab/check-version', { baseUrl })
  }

  async getGitLabAuthUrl(baseUrl: string | null = null) {
    const query = baseUrl ? `?baseUrl=${encodeURIComponent(baseUrl)}` : ''
    return this.get(`/auth/gitlab${query}`)
  }

  async disconnectProvider(provider: string) {
    return this.delete(`/auth/providers/${provider}`)
  }

  async disconnectProviderById(providerId: string) {
    return this.delete(`/auth/provider-connections/${providerId}`)
  }

  async switchGitHubAuthMethod(providerId: string, method: string) {
    return this.post(`/auth/provider-connections/${providerId}/switch-auth`, { method })
  }

  async setPrimaryProvider(providerId: string) {
    return this.post(`/auth/provider-connections/${providerId}/set-primary`)
  }

  async getCurrentUser() {
    return this.get('/auth/me')
  }

  async logout() {
    return this.post('/auth/logout')
  }

  async getProjects() {
    return this.get('/projects')
  }

  async getProject(id: string) {
    return this.get(`/projects/${id}`)
  }

  async createProject(data: Record<string, unknown>) {
    return this.post('/projects', data)
  }

  async updateProject(id: string, data: Record<string, unknown>) {
    return this.put(`/projects/${id}`, data)
  }

  async deleteProject(id: string) {
    return this.delete(`/projects/${id}`)
  }

  async getProjectRepositories(projectId: string): Promise<Repository[]> {
    return this.get<Repository[]>(`/repositories/project/${projectId}`)
  }

  async updateProjectRepositories(projectId: string, repositories: unknown[]): Promise<Repository[]> {
    return this.put<Repository[]>(`/repositories/project/${projectId}`, { repositories })
  }

  async getAvailableRepositories() {
    return this.get('/repositories/available')
  }

  async getProjectPullRequests(projectId: string) {
    return this.get(`/pull-requests/project/${projectId}`)
  }

  async getGitHubAppStatus(projectId: string): Promise<{
    hasGitHubRepos: boolean
    needsInstallation: boolean
    repos: Array<{ repositoryFullName: string; repositoryName: string; installed: boolean }>
    installUrl: string | null
  }> {
    return this.get(`/projects/${projectId}/github-app-status`)
  }

  async getCommitGraph(projectId: string, repoId: string, options: CommitGraphOptions = {}): Promise<CommitGraphData> {
    const params = new URLSearchParams()
    if (options.branch) params.append('branch', options.branch)
    if (options.limit) params.append('limit', options.limit.toString())
    const query = params.toString() ? `?${params.toString()}` : ''
    return this.get<CommitGraphData>(`/projects/${projectId}/repositories/${repoId}/commit-graph${query}`)
  }

  async getBoardConfig(): Promise<BoardConfig> {
    return this.get<BoardConfig>('/projects/board/config')
  }

  async updateProjectViewPreset(projectId: string, viewPreset: string) {
    return this.put(`/projects/${projectId}`, { viewPreset })
  }

  async getPaddleCheckoutData(plan: string) {
    return this.post('/payments/checkout', { plan })
  }

  async createPortalSession() {
    return this.post('/payments/portal')
  }

  async getSubscriptionStatus() {
    return this.get<{
      status: string
      cancelScheduled: boolean
      cancelEffectiveAt: string | null
      currentPeriodEnd: string | null
    }>('/payments/subscription')
  }

  async getConfig() {
    return this.get('/config')
  }

  async addPRComment(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    body: string,
    inReplyToCommentId: string | null = null,
    threadId: string | null = null,
    projectId: string | null = null,
  ) {
    const data: Record<string, unknown> = { body }
    if (inReplyToCommentId) data.inReplyToCommentId = inReplyToCommentId
    if (threadId) data.threadId = threadId
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/comments${q}`,
      data,
    )
  }

  async submitPRReview(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    action: string,
    body: string | null = null,
    projectId: string | null = null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post(`/pull-requests/${provider}/${owner}/${repo}/${number}/reviews${q}`, {
      action,
      body,
    })
  }

  async mergePR(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    method = 'merge',
    commitTitle: string | null = null,
    commitMessage: string | null = null,
    projectId: string | null = null,
  ): Promise<MergeResult> {
    const data: Record<string, unknown> = { method }
    if (commitTitle) data.commitTitle = commitTitle
    if (commitMessage) data.commitMessage = commitMessage
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<MergeResult>(`/pull-requests/${provider}/${owner}/${repo}/${number}/merge${q}`, data)
  }

  async closePR(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    projectId: string | null = null,
  ): Promise<CloseResult> {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<CloseResult>(`/pull-requests/${provider}/${owner}/${repo}/${number}/close${q}`)
  }

  async resolveThread(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    threadId: string,
    resolved: boolean,
    projectId: string | null = null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.put(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/threads/${encodeURIComponent(threadId)}${q}`,
      { resolved },
    )
  }

  async updateComment(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    commentId: string,
    body: string,
    projectId: string | null = null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.patch(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/comments/${encodeURIComponent(commentId)}${q}`,
      { body },
    )
  }

  async deleteComment(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    commentId: string,
    projectId: string | null = null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.delete(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/comments/${encodeURIComponent(commentId)}${q}`,
    )
  }

  async getProviderUserLogin(provider: string): Promise<ProviderLoginResult> {
    return this.get<ProviderLoginResult>(`/pull-requests/${provider}/user`)
  }

  async getPRFiles(provider: string, owner: string, repo: string, number: number, projectId?: string | null): Promise<PRFilesData> {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.get<PRFilesData>(`/pull-requests/${provider}/${owner}/${repo}/${number}/files${q}`)
  }

  async getSinglePR(provider: string, owner: string, repo: string, number: number, projectId?: string | null) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.get(`/pull-requests/${provider}/${owner}/${repo}/${number}${q}`)
  }

  async submitReviewWithComments(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    action: string,
    body: string | null,
    comments: Array<{ path: string; body: string; line?: number }> = [],
    projectId?: string | null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post(`/pull-requests/${provider}/${owner}/${repo}/${number}/reviews${q}`, {
      action,
      body,
      comments,
    })
  }

  async applySuggestionAsBot(params: {
    provider: string
    repositoryFullName: string
    pullRequestNumber: number
    reviewId: string
    findingId: string
    force?: boolean
  }) {
    return this.post<{
      applied: boolean
      alreadyApplied?: boolean
      reviewId?: string
      postedAs?: 'bot' | 'user'
      staleHead?: boolean
      currentSha?: string
      reviewSha?: string
    }>('/ai/apply-suggestion', params)
  }

  async generateCommitMessage(
    params: { body?: string; suggestionCode?: string; originalCode?: string },
    projectId?: string | null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<{ message: string }>(`/ai/commit-message${q}`, params)
  }

  async commitSuggestion(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    commentId: string,
    commitMessage?: string,
    projectId?: string | null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<{ sha: string; message: string; alreadyApplied?: boolean }>(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/commit-suggestion${q}`,
      { commentId, commitMessage },
    )
  }

  async commitSuggestionDirect(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    data: {
      filePath: string
      line: number
      endLine?: number
      suggestionCode: string
      commitMessage?: string
    },
    projectId?: string | null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<{ sha: string; message: string; alreadyApplied?: boolean }>(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/commit-suggestion-direct${q}`,
      data,
    )
  }

  async postSuggestionComment(
    provider: string,
    owner: string,
    repo: string,
    number: number,
    data: {
      filePath: string
      line: number
      endLine?: number
      suggestionCode: string
      body?: string
      findingId?: string
      reviewKnowledgeId?: string
    },
    projectId?: string | null,
  ) {
    const q = projectId ? `?projectId=${projectId}` : ''
    return this.post<{ reviewId: string; htmlUrl: string; postedComments: number; alreadyApplied?: boolean }>(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/add-suggestion-comment${q}`,
      data,
    )
  }

  async getSuggestionActions(
    provider: string,
    owner: string,
    repo: string,
    number: number,
  ) {
    return this.get<Array<{
      id: string
      provider: string
      repositoryFullName: string
      pullRequestNumber: number
      actionType: string
      fingerprint: string
      status: string
      filePath?: string
      lineNumber?: number
      endLineNumber?: number
      suggestionPreview?: string
      reviewKnowledgeId?: string
      findingId?: string
      sourceRef?: string
      resultSha?: string
      resultCommentId?: string
      resultThreadId?: string
      performedBy: string
      createdAt: string
      completedAt?: string
    }>>(
      `/pull-requests/${provider}/${owner}/${repo}/${number}/suggestion-actions`,
    )
  }

  async updateFindingSuggestedFix(reviewId: string, findingId: string, suggestedFix: string) {
    return this.request<unknown>('PATCH', `/ai/reviews/${reviewId}/findings/${findingId}/suggested-fix`, { suggestedFix })
  }

  async estimateBCU(params: Record<string, unknown>) {
    return this.request('POST', '/ai/estimate', params)
  }

  async createAIReview(data: Record<string, unknown>): Promise<AIReviewType> {
    return this.post<AIReviewType>('/ai/reviews', data)
  }

  async createPRInsight(data: {
    repositoryFullName: string
    pullRequestNumber: number
    provider: string
    insightType: string
    projectId?: string
    customQuestion?: string
    forceRefresh?: boolean
  }) {
    return this.post<{
      id: string
      insightType: string
      title: string
      content: string
      highlights: Array<{ file: string; description: string }>
      relatedFiles: string[]
      headSha: string
      createdAt: string
      bcuCost?: number
      cached: boolean
    }>('/ai/insights', data)
  }

  async getPRInsights(params: {
    provider: string
    repositoryFullName: string
    pullRequestNumber: number
  }) {
    const qs = new URLSearchParams({
      provider: params.provider,
      repositoryFullName: params.repositoryFullName,
      pullRequestNumber: String(params.pullRequestNumber),
    }).toString()
    return this.get<Array<{
      id: string
      insightType: string
      title: string
      content: string
      highlights: Array<{ file: string; description: string }>
      relatedFiles: string[]
      headSha: string
      customQuestion?: string
      createdAt: string
      bcuCost?: number
    }>>(`/ai/insights?${qs}`)
  }

  async analyzeCIFailure(data: {
    repositoryFullName: string
    pullRequestNumber: number
    provider: string
    projectId?: string
    autoPostComment?: boolean
    forceRefresh?: boolean
  }) {
    return this.post<{
      id: string
      summary: string
      totalFailures: number
      rootCauses: Array<{
        jobName: string
        category: string
        title: string
        description: string
        causedByPR: boolean
        affectedFiles: string[]
        evidence: string
        fixSuggestion: {
          description: string
          code?: string
          file?: string
          confidence: 'high' | 'medium' | 'low'
        }
      }>
      overallSeverity: 'critical' | 'warning' | 'info'
      isFlaky: boolean
      recommendedActions: string[]
      commentPosted?: boolean
      commentId?: string
      headSha: string
      createdAt: string
      bcuCost?: number
      cached: boolean
    }>('/ai/ci-analysis', data)
  }

  async getCIAnalyses(params: {
    provider: string
    repositoryFullName: string
    pullRequestNumber: number
  }) {
    const qs = new URLSearchParams({
      provider: params.provider,
      repositoryFullName: params.repositoryFullName,
      pullRequestNumber: String(params.pullRequestNumber),
    }).toString()
    return this.get<Array<{
      id: string
      summary: string
      totalFailures: number
      rootCauses: Array<{
        jobName: string
        category: string
        title: string
        description: string
        causedByPR: boolean
        affectedFiles: string[]
        evidence: string
        fixSuggestion: {
          description: string
          code?: string
          file?: string
          confidence: 'high' | 'medium' | 'low'
        }
      }>
      overallSeverity: 'critical' | 'warning' | 'info'
      isFlaky: boolean
      recommendedActions: string[]
      commentPosted?: boolean
      commentId?: string
      headSha: string
      createdAt: string
      bcuCost?: number
    }>>(`/ai/ci-analyses?${qs}`)
  }

  async getAIReviews(query: AIReviewQuery = {}): Promise<AIReviewType[]> {
    const params = new URLSearchParams()
    if (query.provider) params.append('provider', query.provider)
    if (query.repositoryFullName) params.append('repositoryFullName', query.repositoryFullName)
    if (query.pullRequestNumber)
      params.append('pullRequestNumber', String(query.pullRequestNumber))
    const queryString = params.toString()
    return this.get<AIReviewType[]>(`/ai/reviews${queryString ? '?' + queryString : ''}`)
  }

  async getAIReview(reviewId: string) {
    return this.get(`/ai/reviews/${reviewId}`)
  }

  async getActiveReviewJob(provider: string, repositoryFullName: string, pullRequestNumber: number) {
    const params = new URLSearchParams({ provider, repositoryFullName, pullRequestNumber: String(pullRequestNumber) })
    return this.get<{
      id: string
      userId: string
      projectId: string | null
      provider: string
      repositoryFullName: string
      pullRequestNumber: number
      status: 'pending' | 'running' | 'complete' | 'error'
      progressPercent: number
      progressStep: string | null
      reviewKnowledgeId: string | null
      errorMessage: string | null
      createdAt: string
      startedAt: string | null
      completedAt: string | null
    } | null>(`/ai/reviews/active-job?${params}`)
  }

  async cancelReviewJob(provider: string, repositoryFullName: string, pullRequestNumber: number) {
    const params = new URLSearchParams({ provider, repositoryFullName, pullRequestNumber: String(pullRequestNumber) })
    return this.post<{ success: boolean }>(`/ai/reviews/cancel-job?${params}`)
  }

  async cancelChatSession(provider: string, repositoryFullName: string, pullRequestNumber: number) {
    const params = new URLSearchParams({ provider, repositoryFullName, pullRequestNumber: String(pullRequestNumber) })
    return this.post<{ success: boolean }>(`/ai/chat/cancel?${params}`)
  }

  // ── Coddy AI v2: Chat + Knowledge ──

  async chatWithReview(reviewId: string, data: {
    message: string
    contextScope?: 'full_pr' | 'file' | 'line' | 'finding'
    filePath?: string
    lineStart?: number
    lineEnd?: number
    findingId?: string
    conversationId?: string
    quote?: {
      findingId: string
      path: string
      line: number
      severity: string
      category: string
      body: string
    }
  }) {
    return this.post<{
      conversation: {
        id: string
        reviewKnowledgeId: string
        userId: string
        contextScope: string
        messages: Array<{
          role: 'user' | 'assistant'
          content: string
          timestamp: string
          quote?: {
            findingId: string
            path: string
            line: number
            severity: string
            category: string
            body: string
          }
        }>
        createdAt: string
        updatedAt: string
      }
      assistantMessage: string
    }>(`/ai/reviews/${reviewId}/chat`, data)
  }

  async getConversations(reviewId: string) {
    return this.get<Array<{
      id: string
      reviewKnowledgeId: string
      contextScope: string
      messages: Array<{
        role: 'user' | 'assistant'
        content: string
        timestamp: string
      }>
      createdAt: string
    }>>(`/ai/reviews/${reviewId}/conversations`)
  }

  async getConversationsByPR(provider: string, repositoryFullName: string, prNumber: number) {
    const params = new URLSearchParams({ provider, repositoryFullName, prNumber: String(prNumber) })
    return this.get<Array<{
      id: string
      reviewKnowledgeId: string | null
      contextScope: string
      messages: Array<{
        role: 'user' | 'assistant'
        content: string
        timestamp: string
      }>
      createdAt: string
    }>>(`/ai/pr-conversations?${params}`)
  }

  async getConversation(reviewId: string, conversationId: string) {
    return this.get<{
      id: string
      reviewKnowledgeId: string
      contextScope: string
      messages: Array<{
        role: 'user' | 'assistant'
        content: string
        timestamp: string
      }>
      createdAt: string
    }>(`/ai/reviews/${reviewId}/conversations/${conversationId}`)
  }

  async resolveFinding(reviewId: string, findingId: string, resolveOnProvider = false) {
    return this.post<{
      findingId: string
      resolvedAt: string
      providerResolved: boolean
    }>(`/ai/reviews/${reviewId}/findings/${findingId}/resolve`, { resolveOnProvider })
  }

  async deleteFinding(reviewId: string, findingId: string, deleteOnProvider = false) {
    const q = deleteOnProvider ? '?deleteOnProvider=true' : ''
    return this.delete<{
      findingId: string
      providerDeleted: boolean
    }>(`/ai/reviews/${reviewId}/findings/${findingId}${q}`)
  }

  async getPatterns(provider: string, repositoryFullName: string) {
    const params = new URLSearchParams({ provider, repositoryFullName })
    return this.get<{
      id: string
      repositoryFullName: string
      provider: string
      patternType: string
      category: string
      description: string
      occurrenceCount: number
      exampleReviewIds: string[]
      recommendation: string | null
      isActive: boolean
      createdAt: string
      updatedAt: string
    }[]>(`/ai/patterns?${params}`)
  }

  async dismissPattern(patternId: string) {
    return this.post(`/ai/patterns/${patternId}/dismiss`, {})
  }

  async getReviewMeta(params: {
    provider: string
    repositoryFullName: string
    pullRequestNumber: number
  }): Promise<{
    hasReview: boolean
    reviewId?: string
    verdict?: string
    findingCount?: number
    unresolvedCount?: number
    conversationCount?: number
    lastReviewedAt?: string
    categoryCounts?: Record<string, number>
    severityCounts?: Record<string, number>
  }> {
    const qs = new URLSearchParams({
      provider: params.provider,
      repositoryFullName: params.repositoryFullName,
      pullRequestNumber: String(params.pullRequestNumber),
    })
    return this.get(`/ai/reviews/meta?${qs}`)
  }

  async getAISettings() {
    return this.get('/ai/settings')
  }

  async updateAISettings(data: Record<string, unknown>) {
    return this.put('/ai/settings', data)
  }

  async validateProviderApiKey(provider: string, apiKey: string) {
    return this.post<{ valid: boolean }>('/ai/validate-api-key', { provider, apiKey })
  }

  async getProjectContext(projectId: string): Promise<AIContext | null> {
    return this.get<AIContext | null>(`/ai/projects/${projectId}/context`)
  }

  async generateProjectContext(projectId: string, data: Record<string, unknown> = {}) {
    return this.post(`/ai/projects/${projectId}/context/generate`, data)
  }

  async getContextStatus(contextId: string) {
    return this.get(`/ai/context/${contextId}/status`)
  }

  async updateProjectContext(projectId: string, finalPromptTemplate: string) {
    return this.put(`/ai/projects/${projectId}/context`, { finalPromptTemplate })
  }

  async getProjectBranches(projectId: string): Promise<Record<string, BranchInfo[]>> {
    return this.get<Record<string, BranchInfo[]>>(`/ai/projects/${projectId}/branches`)
  }

  async getRepositoryStats(projectId: string, branches: Record<string, string>): Promise<RepositoryStatsData> {
    return this.post<RepositoryStatsData>(`/ai/projects/${projectId}/repository-stats`, { branches })
  }

  async getAnalyticsOverview(projectId: string, days = 30) {
    return this.get(`/projects/${projectId}/analytics/overview?days=${days}`)
  }

  async getAnalyticsCycleTime(projectId: string, days = 30, repo: string | null = null) {
    let url = `/projects/${projectId}/analytics/cycle-time?days=${days}`
    if (repo) url += `&repo=${encodeURIComponent(repo)}`
    return this.get(url)
  }

  async getAnalyticsTeam(projectId: string, weeks = 12) {
    return this.get(`/projects/${projectId}/analytics/team?weeks=${weeks}`)
  }

  async getAnalyticsInsights(projectId: string, limit = 10) {
    return this.get(`/projects/${projectId}/analytics/insights?limit=${limit}`)
  }

  async dismissInsight(projectId: string, insightId: string) {
    return this.post(`/projects/${projectId}/analytics/insights/${insightId}/dismiss`)
  }

  async generateInsights(
    projectId: string,
    body?: { smartModelId?: string | null; smartModelProvider?: string | null },
  ) {
    return this.post(`/projects/${projectId}/analytics/insights/generate`, body ?? {})
  }

  async syncAnalytics(projectId: string) {
    return this.post(`/projects/${projectId}/analytics/sync`)
  }

  async getAnalyticsSyncStatus(projectId: string) {
    return this.get(`/projects/${projectId}/analytics/sync/status`)
  }

  async getAnalyticsContributors(projectId: string, days = 30) {
    return this.get(`/projects/${projectId}/analytics/contributors?days=${days}`)
  }

  async clearAnalytics(projectId: string) {
    return this.delete(`/projects/${projectId}/analytics`)
  }

  // ── Sprint Configuration & Summaries ──

  async getSprintConfig(projectId: string) {
    return this.get(`/projects/${projectId}/analytics/sprint/config`)
  }

  async saveSprintConfig(projectId: string, config: Record<string, unknown>) {
    return this.put(`/projects/${projectId}/analytics/sprint/config`, config)
  }

  async getSprintSummaries(projectId: string, type?: string, limit?: number) {
    const params = new URLSearchParams()
    if (type) params.set('type', type)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return this.get(`/projects/${projectId}/analytics/sprint/summaries${qs ? `?${qs}` : ''}`)
  }

  async getSprintSummary(projectId: string, summaryId: string) {
    return this.get(`/projects/${projectId}/analytics/sprint/summaries/${summaryId}`)
  }

  async generateSprintSummary(projectId: string, summaryType: string) {
    return this.post(`/projects/${projectId}/analytics/sprint/summaries/generate`, { summaryType })
  }

  async dismissSprintSummary(projectId: string, summaryId: string) {
    return this.post(`/projects/${projectId}/analytics/sprint/summaries/${summaryId}/dismiss`)
  }

  async getUsageStatus(): Promise<UsageStatusData> {
    return this.get<UsageStatusData>('/billing/usage-status')
  }

  async setUsageMode(enabled: boolean, monthlyLimitBcu: number | null = null) {
    return this.put('/billing/usage-mode', { enabled, monthlyLimitBcu })
  }

  async setSpendingLimit(limitBcu: number | null) {
    return this.put('/billing/spending-limit', { limitBcu })
  }

  async getUsageHistory(params: {
    page?: number
    limit?: number
    startDate?: string | null
    endDate?: string | null
  } = {}): Promise<PaginatedUsageHistory> {
    const qs = new URLSearchParams()
    if (params.page) qs.append('page', params.page.toString())
    if (params.limit) qs.append('limit', params.limit.toString())
    if (params.startDate) qs.append('startDate', params.startDate)
    if (params.endDate) qs.append('endDate', params.endDate)
    const queryString = qs.toString()
    return this.get<PaginatedUsageHistory>(`/billing/usage-history${queryString ? '?' + queryString : ''}`)
  }

  async getChargesHistory(params: {
    page?: number
    limit?: number
  } = {}): Promise<PaginatedChargesHistory> {
    const qs = new URLSearchParams()
    if (params.page) qs.append('page', params.page.toString())
    if (params.limit) qs.append('limit', params.limit.toString())
    const queryString = qs.toString()
    return this.get<PaginatedChargesHistory>(`/billing/charges-history${queryString ? '?' + queryString : ''}`)
  }

  async getBillingPresets() {
    return this.get('/billing/presets')
  }

  async createUsageCharge() {
    return this.post('/billing/charge')
  }

  async searchCollaborator(projectId: string, email: string): Promise<CollaboratorSearchResult> {
    return this.post<CollaboratorSearchResult>(`/projects/${projectId}/collaborators/search`, { email })
  }

  async getCollaborators(projectId: string): Promise<Collaborator[]> {
    return this.get<Collaborator[]>(`/projects/${projectId}/collaborators`)
  }

  async addCollaborator(projectId: string, userId: string, role: string) {
    return this.post(`/projects/${projectId}/collaborators`, { userId, role })
  }

  async updateCollaboratorRole(projectId: string, userId: string, role: string) {
    return this.put(`/projects/${projectId}/collaborators/${userId}`, { role })
  }

  async removeCollaborator(projectId: string, userId: string) {
    return this.delete(`/projects/${projectId}/collaborators/${userId}`)
  }
}

export const api = new ApiClient()
